import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { APIError } from '@cold-ambulance/client';
import { phoneValidator } from '@cold-ambulance/isomorphic';
import { AuthLayout } from '@cold-ambulance/layout';
import { Button, CardContent, Input, Label } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  PhoneInput,
  errorToFormIssue,
  useAction,
} from '@cold-ambulance/ui';

import { type Step, StepCircles } from './stepper.tsx';

const schema = z.object({
  firstName: z.string().min(1, { message: 'اسم جهة الاتصال مطلوب' }),
  lastName: z.string().min(1, { message: 'اسم العائلة لجهة الاتصال مطلوب' }),
  jobTitle: z.string().min(1, { message: 'المسمى الوظيفي مطلوب' }),
  phoneNumber: z.string().min(1, { message: 'مطلوب رقم الجوال' }),
});
const phoneNumberValidator = schema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);
export default function SignupContact({
  onSubmitSuccess,
  onSignOut,
  step,
  index,
  newUser,
}: {
  onSubmitSuccess: () => void;
  onSignOut?: () => void;
  step: Step;
  index: number;
  newUser?: boolean;
}) {
  const { t } = useTranslation();
  const updateVendorMutation = useAction('PATCH /vendor/me', {
    onSuccess: onSubmitSuccess,
  });

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      phoneNumber: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateVendorMutation.mutateAsync(value);
          return void 0;
        } catch (error) {
          if (error instanceof APIError) {
            if (error.status === 409) {
              return errorToFormIssue(new Error(t('signup.phoneInUse')));
            }
          }
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      title={t('signup.registerWithUs')}
      className="border"
      onBack={onSignOut}
      backLabel={t('header.logout')}
    >
      <StepCircles step={step} index={index} />
      <p className="mt-6 font-bold">{t('signup.contactPerson')}</p>

      <CardContent className="px-0">
        <form
          className="mt-4 mb-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field
              name="firstName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="firstName">
                    {t('profile.firstName')}
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder={t('placeholders.firstName')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
            <form.Field
              name="lastName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="lastName">
                    {t('profile.lastName')}
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder={t('placeholders.lastName')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Field
            name="jobTitle"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="jobTitle">
                  {t('profile.contactJobTitle')}
                </Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder={t('placeholders.projectManager')}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="phoneNumber"
            validators={{
              onBlurAsync: phoneNumberValidator,
            }}
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="phone">
                  {t('profile.phoneNumber')}
                </Label>
                <PhoneInput
                  id="phone"
                  dir="ltr"
                  className="rtl:flex-row-reverse"
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  international
                  defaultCountry="SA"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-8 w-full cursor-pointer"
              >
                {isSubmitting ? t('signup.continuing') : t('common.next')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
